import { alveolarPressure, ambientToDepth, depthToAmbient } from "../physics/pressure.js";
import { inspiredO2 } from "../physics/gas.js";
import { cnsAtConstantPO2, cnsLinearSegment } from "../oxygen/cns.js";
import { otuAtConstantPO2, otuLinearSegment } from "../oxygen/otu.js";
import type {
  DecoStop,
  GasMix,
  PlanInput,
  PlanResult,
  PlannedSegment,
  TissueSnapshot,
} from "../types.js";
import { N_COMPARTMENTS, ZHL16C, kN2 } from "./constants.js";
import { applySegment, type DepthSegment } from "./schreiner.js";
import {
  ceilingAmbientForCompartment,
  gfAtDepth,
  overallCeilingAmbient,
} from "./m-values.js";
import { ndlAtDepth } from "./ndl.js";
import { initSurfaceEquilibrium } from "./tissues.js";

const EPSILON = 1e-9;

function gasAtSegment(idx: number, gases: GasMix[], schedule: PlanInput["gasSchedule"]): number {
  let active = 0;
  for (const a of schedule) {
    if (a.startSegmentIdx <= idx) active = a.gasIdx;
  }
  if (active < 0 || active >= gases.length) active = 0;
  return active;
}

function roundUpToStop(depth: number, stopIncrement: number, lastStopDepth: number): number {
  if (depth <= 0) return 0;
  if (depth <= lastStopDepth + EPSILON) return lastStopDepth;
  const above = lastStopDepth + Math.ceil((depth - lastStopDepth) / stopIncrement) * stopIncrement;
  return above;
}

function nextShallowerStop(currentStop: number, stopIncrement: number, lastStopDepth: number): number {
  if (currentStop <= lastStopDepth + EPSILON) return 0;
  const next = currentStop - stopIncrement;
  if (next < lastStopDepth - EPSILON) return lastStopDepth;
  return Math.max(0, next);
}

function stopDurationForCompartment(
  p0: number,
  pInspired: number,
  k: number,
  pMax: number,
): number {
  if (p0 <= pMax + EPSILON) return 0;
  if (pInspired >= pMax - EPSILON) return Infinity;
  return -Math.log((pMax - pInspired) / (p0 - pInspired)) / k;
}

function requiredStopDuration(
  state: TissueSnapshot,
  stopDepth: number,
  gas: GasMix,
  nextStopDepth: number,
  gfNextStop: number,
): number {
  const pInspired = alveolarPressure(stopDepth) * gas.fN2;
  const pAmbNext = depthToAmbient(nextStopDepth);
  let maxT = 0;
  for (let i = 0; i < N_COMPARTMENTS; i++) {
    const cpt = ZHL16C[i]!;
    const k = kN2(cpt);
    const pMax = pAmbNext * (gfNextStop / cpt.bN2 - gfNextStop + 1) + gfNextStop * cpt.aN2;
    const t = stopDurationForCompartment(state.pN2[i]!, pInspired, k, pMax);
    if (t > maxT) maxT = t;
  }
  return maxT;
}

interface RunningTotals {
  cns: number;
  otu: number;
  time: number;
}

function recordSegment(
  segs: PlannedSegment[],
  totals: RunningTotals,
  kind: PlannedSegment["kind"],
  startDepth: number,
  endDepth: number,
  duration: number,
  gas: GasMix,
  gasIdx: number,
  stateAfter: TissueSnapshot,
): void {
  if (duration <= 0) return;
  const startPO2 = inspiredO2(startDepth, gas);
  const endPO2 = inspiredO2(endDepth, gas);

  if (startDepth === endDepth) {
    totals.otu += otuAtConstantPO2(startPO2, duration);
    totals.cns += cnsAtConstantPO2(startPO2, duration);
  } else {
    totals.otu += otuLinearSegment(startDepth, endDepth, duration, gas);
    totals.cns += cnsLinearSegment(startDepth, endDepth, duration, gas);
  }

  segs.push({
    kind,
    startDepth,
    endDepth,
    duration,
    gasIdx,
    startTime: totals.time,
    endTime: totals.time + duration,
    startPO2,
    endPO2,
    tissuesAfter: { pN2: [...stateAfter.pN2] },
  });
  totals.time += duration;
}

export function plan(input: PlanInput): PlanResult {
  if (input.waypoints.length === 0) throw new Error("At least one waypoint is required");
  if (input.gases.length === 0) throw new Error("At least one gas is required");

  const gfLow = input.gradientFactors.low / 100;
  const gfHigh = input.gradientFactors.high / 100;

  let state = initSurfaceEquilibrium();
  const segments: PlannedSegment[] = [];
  const totals: RunningTotals = { cns: 0, otu: 0, time: 0 };

  let currentDepth = 0;
  let segCounter = 0;

  for (let wIdx = 0; wIdx < input.waypoints.length; wIdx++) {
    const wp = input.waypoints[wIdx]!;

    if (Math.abs(wp.depth - currentDepth) > EPSILON) {
      const transitionGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const transitionGas = input.gases[transitionGasIdx]!;
      const isDescent = wp.depth > currentDepth;
      const rate = isDescent ? input.descentRate : input.ascentRate;
      const dt = Math.abs(wp.depth - currentDepth) / rate;
      state = applySegment(state, {
        startDepth: currentDepth,
        endDepth: wp.depth,
        duration: dt,
        gas: transitionGas,
      });
      recordSegment(
        segments,
        totals,
        isDescent ? "descent" : "ascent",
        currentDepth,
        wp.depth,
        dt,
        transitionGas,
        transitionGasIdx,
        state,
      );
      currentDepth = wp.depth;
      segCounter++;
    }

    if (wp.duration > 0) {
      const bottomGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const bottomGas = input.gases[bottomGasIdx]!;
      state = applySegment(state, {
        startDepth: wp.depth,
        endDepth: wp.depth,
        duration: wp.duration,
        gas: bottomGas,
      });
      recordSegment(
        segments,
        totals,
        "bottom",
        wp.depth,
        wp.depth,
        wp.duration,
        bottomGas,
        bottomGasIdx,
        state,
      );
      segCounter++;
    }
  }

  const decoGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
  const decoGas = input.gases[decoGasIdx]!;

  const ndl = ndlAtDepth(state, currentDepth, decoGas, gfHigh);

  const ceilingAmbAtGfLow = overallCeilingAmbient(state, gfLow);
  const ceilingDepthAtGfLow = Math.max(0, ambientToDepth(ceilingAmbAtGfLow));
  const firstStopDepth = roundUpToStop(ceilingDepthAtGfLow, input.stopIncrement, input.lastStopDepth);

  const decoStops: DecoStop[] = [];
  const decoStartTime = totals.time;

  if (firstStopDepth <= 0) {
    if (currentDepth > 0) {
      const dt = currentDepth / input.ascentRate;
      const segGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const segGas = input.gases[segGasIdx]!;
      state = applySegment(state, {
        startDepth: currentDepth,
        endDepth: 0,
        duration: dt,
        gas: segGas,
      });
      recordSegment(segments, totals, "ascent", currentDepth, 0, dt, segGas, segGasIdx, state);
      currentDepth = 0;
      segCounter++;
    }
  } else {
    if (currentDepth > firstStopDepth) {
      const dt = (currentDepth - firstStopDepth) / input.ascentRate;
      const segGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const segGas = input.gases[segGasIdx]!;
      state = applySegment(state, {
        startDepth: currentDepth,
        endDepth: firstStopDepth,
        duration: dt,
        gas: segGas,
      });
      recordSegment(
        segments,
        totals,
        "ascent",
        currentDepth,
        firstStopDepth,
        dt,
        segGas,
        segGasIdx,
        state,
      );
      currentDepth = firstStopDepth;
      segCounter++;
    }

    let stopDepth = firstStopDepth;
    let safety = 0;
    while (stopDepth > 0 && safety++ < 200) {
      const nextStop = nextShallowerStop(stopDepth, input.stopIncrement, input.lastStopDepth);
      const gfNext = gfAtDepth(nextStop, firstStopDepth, gfLow, gfHigh);
      const stopGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const stopGas = input.gases[stopGasIdx]!;

      const exactT = requiredStopDuration(state, stopDepth, stopGas, nextStop, gfNext);
      const stopTime = Math.ceil(exactT - 1e-6);

      if (stopTime > 0) {
        state = applySegment(state, {
          startDepth: stopDepth,
          endDepth: stopDepth,
          duration: stopTime,
          gas: stopGas,
        });
        recordSegment(
          segments,
          totals,
          "stop",
          stopDepth,
          stopDepth,
          stopTime,
          stopGas,
          stopGasIdx,
          state,
        );
        decoStops.push({ depth: stopDepth, duration: stopTime, gasIdx: stopGasIdx });
        segCounter++;
      }

      const ascendDt = (stopDepth - nextStop) / input.ascentRate;
      const ascentGasIdx = gasAtSegment(segCounter, input.gases, input.gasSchedule);
      const ascentGas = input.gases[ascentGasIdx]!;
      state = applySegment(state, {
        startDepth: stopDepth,
        endDepth: nextStop,
        duration: ascendDt,
        gas: ascentGas,
      });
      recordSegment(
        segments,
        totals,
        "ascent",
        stopDepth,
        nextStop,
        ascendDt,
        ascentGas,
        ascentGasIdx,
        state,
      );
      segCounter++;
      stopDepth = nextStop;
    }
    currentDepth = 0;
  }

  const ceilingDepthAtGfLowSnapshot = ceilingDepthAtGfLow;

  let maxDepth = 0;
  let maxDepthGasIdx = 0;
  let bottomTime = 0;
  let runningSegIdx = 0;
  for (let wIdx = 0; wIdx < input.waypoints.length; wIdx++) {
    const wp = input.waypoints[wIdx]!;
    if (Math.abs(wp.depth - (wIdx === 0 ? 0 : input.waypoints[wIdx - 1]!.depth)) > EPSILON) {
      runningSegIdx++;
    }
    if (wp.depth > maxDepth) {
      maxDepth = wp.depth;
      maxDepthGasIdx = gasAtSegment(runningSegIdx, input.gases, input.gasSchedule);
    }
    if (wp.duration > 0) {
      bottomTime += wp.duration;
      runningSegIdx++;
    }
  }
  const maxDepthGas = input.gases[maxDepthGasIdx] ?? input.gases[0]!;
  const noStopLimit = ndlAtDepth(initSurfaceEquilibrium(), maxDepth, maxDepthGas, gfHigh);

  return {
    segments,
    decoStops,
    ndlMinutes: ndl !== null && Number.isFinite(ndl) ? Math.floor(ndl) : null,
    noStopLimitAtMaxDepth:
      noStopLimit !== null && Number.isFinite(noStopLimit) ? Math.floor(noStopLimit) : null,
    maxDepth,
    bottomTime,
    isNoDeco: decoStops.length === 0,
    totalRuntime: totals.time,
    timeToSurface: totals.time - decoStartTime,
    cnsPercent: totals.cns,
    otu: totals.otu,
    ceilingsAtBottomExit: { depth: ceilingDepthAtGfLowSnapshot, gfFirstStop: gfLow },
    finalTissues: { pN2: [...state.pN2] },
  };
}

// Re-export helpers for testing
export { ceilingAmbientForCompartment };
